import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export interface ChatMessage {
  id: string;
  content: string;
  type: 'text' | 'system';
  messageType?: 'TEXT' | 'EMOJI' | 'GIF' | 'IMAGE';
  gifUrl?: string | null;
  gifId?: string | null;
  groupId: string;
  userId: string;
  username: string;
  userProfilePhoto: string | null;
  userSuspendedAt?: Date | string | null | undefined;
  createdAt: string;
  // Message status for optimistic updates
  status?: 'sending' | 'sent' | 'failed';
  tempId?: string; // Temporary ID for optimistic updates
}

export interface OnlineUser {
  id: string;
  username: string;
  profilePhoto: string | null;
}

interface ChatState {
  // Connection state
  isConnected: boolean;
  connectionError: string | null;
  isReconnecting: boolean;
  
  // Messages state  
  messages: Record<string, ChatMessage[]>; // groupId -> messages
  isLoadingHistory: boolean;
  isLoadingMore: boolean; // For pagination
  messageHistory: Record<string, boolean>; // groupId -> hasLoadedHistory
  hasMoreMessages: Record<string, boolean>; // groupId -> hasMoreMessages
  
  // Online users state
  onlineUsers: Record<string, OnlineUser[]>; // groupId -> online users
  
  // Current active group
  currentGroupId: string | null;
  
  // UI state
  isTyping: boolean;
  lastMessageTime: Record<string, number>; // groupId -> timestamp
}

interface ChatActions {
  // Connection actions
  setConnectionStatus: (connected: boolean, error?: string | null) => void;
  setReconnecting: (reconnecting: boolean) => void;
  
  // Message actions
  addMessage: (groupId: string, message: ChatMessage) => void;
  setMessages: (groupId: string, messages: ChatMessage[]) => void;
  prependMessages: (groupId: string, messages: ChatMessage[]) => void;
  setLoadingHistory: (loading: boolean) => void;
  setHistoryLoaded: (groupId: string, loaded: boolean) => void;
  
  // Online users actions
  setOnlineUsers: (groupId: string, users: OnlineUser[]) => void;
  addOnlineUser: (groupId: string, user: OnlineUser) => void;
  removeOnlineUser: (groupId: string, userId: string) => void;
  
  // Group actions
  setCurrentGroup: (groupId: string | null) => void;
  clearGroupData: (groupId: string) => void;
  
  // UI actions
  setTyping: (typing: boolean) => void;
  updateLastMessageTime: (groupId: string) => void;
  
  // Optimistic message updates
  addOptimisticMessage: (groupId: string, message: Omit<ChatMessage, 'status' | 'tempId'>) => string;
  updateMessageStatus: (groupId: string, tempId: string, realMessage: ChatMessage) => void;
  markMessageSent: (groupId: string, tempId: string) => void;
  markMessageFailed: (groupId: string, tempId: string) => void;
  removeOptimisticMessage: (groupId: string, tempId: string) => void;
  
  // Utilities  
  clearAllData: () => void;
  getGroupMessages: (groupId: string) => ChatMessage[];
  getGroupOnlineUsers: (groupId: string) => OnlineUser[];
  isHistoryLoaded: (groupId: string) => boolean;
  hasMoreHistoryMessages: (groupId: string) => boolean;
  loadChatHistory: (groupId: string) => Promise<void>;
  loadMoreMessages: (groupId: string) => Promise<void>;
  // Merge freshly fetched server messages into a group without disturbing
  // existing (incl. optimistic) messages. Used to backfill anything missed
  // while the socket was disconnected.
  mergeServerMessages: (groupId: string, messages: ChatMessage[]) => void;
  refetchRecentMessages: (groupId: string) => Promise<void>;
}

type ChatStore = ChatState & ChatActions;

const initialState: ChatState = {
  isConnected: false,
  connectionError: null,
  isReconnecting: false,
  messages: {},
  isLoadingHistory: false,
  isLoadingMore: false,
  messageHistory: {},
  hasMoreMessages: {},
  onlineUsers: {},
  currentGroupId: null,
  isTyping: false,
  lastMessageTime: {},
};

export const useChatStore = create<ChatStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Connection actions
    setConnectionStatus: (connected, error = null) => {
      set({ 
        isConnected: connected, 
        connectionError: error,
        isReconnecting: false 
      });
    },

    setReconnecting: (reconnecting) => {
      set({ isReconnecting: reconnecting });
    },

    // Message actions
    addMessage: (groupId, message) => {
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        
        // Avoid duplicate messages
        if (groupMessages.find(m => m.id === message.id)) {
          return state;
        }
        
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: [...groupMessages, message].sort((a, b) => 
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            ),
          },
          lastMessageTime: {
            ...state.lastMessageTime,
            [groupId]: Date.now(),
          },
        };
      });
    },

    setMessages: (groupId, messages) => {
      set((state) => ({
        ...state,
        messages: {
          ...state.messages,
          [groupId]: messages.sort((a, b) => 
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          ),
        },
      }));
    },

    prependMessages: (groupId, messages) => {
      set((state) => {
        const existingMessages = state.messages[groupId] || [];
        const combinedMessages = [...messages, ...existingMessages];
        
        // Remove duplicates based on message ID
        const uniqueMessages = combinedMessages.reduce((acc, message) => {
          if (!acc.find(m => m.id === message.id)) {
            acc.push(message);
          }
          return acc;
        }, [] as ChatMessage[]);
        
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: uniqueMessages.sort((a, b) => 
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            ),
          },
        };
      });
    },

    setLoadingHistory: (loading) => {
      set({ isLoadingHistory: loading });
    },

    setHistoryLoaded: (groupId, loaded) => {
      set((state) => ({
        ...state,
        messageHistory: {
          ...state.messageHistory,
          [groupId]: loaded,
        },
      }));
    },

    // Online users actions
    setOnlineUsers: (groupId, users) => {
      set((state) => ({
        ...state,
        onlineUsers: {
          ...state.onlineUsers,
          [groupId]: users,
        },
      }));
    },

    addOnlineUser: (groupId, user) => {
      set((state) => {
        const groupUsers = state.onlineUsers[groupId] || [];
        
        // Avoid duplicates
        if (groupUsers.find(u => u.id === user.id)) {
          return state;
        }
        
        return {
          ...state,
          onlineUsers: {
            ...state.onlineUsers,
            [groupId]: [...groupUsers, user].sort((a, b) => 
              a.username.localeCompare(b.username)
            ),
          },
        };
      });
    },

    removeOnlineUser: (groupId, userId) => {
      set((state) => ({
        ...state,
        onlineUsers: {
          ...state.onlineUsers,
          [groupId]: (state.onlineUsers[groupId] || []).filter(u => u.id !== userId),
        },
      }));
    },

    // Group actions
    setCurrentGroup: (groupId) => {
      set({ currentGroupId: groupId });
    },

    clearGroupData: (groupId) => {
      set((state) => {
        const newMessages = { ...state.messages };
        const newOnlineUsers = { ...state.onlineUsers };
        const newMessageHistory = { ...state.messageHistory };
        const newLastMessageTime = { ...state.lastMessageTime };
        
        delete newMessages[groupId];
        delete newOnlineUsers[groupId];
        delete newMessageHistory[groupId];
        delete newLastMessageTime[groupId];
        
        return {
          ...state,
          messages: newMessages,
          onlineUsers: newOnlineUsers,
          messageHistory: newMessageHistory,
          lastMessageTime: newLastMessageTime,
        };
      });
    },

    // UI actions
    setTyping: (typing) => {
      set({ isTyping: typing });
    },

    updateLastMessageTime: (groupId) => {
      set((state) => ({
        ...state,
        lastMessageTime: {
          ...state.lastMessageTime,
          [groupId]: Date.now(),
        },
      }));
    },

    // Optimistic message updates
    addOptimisticMessage: (groupId, message) => {
      const optimisticMessage = {
        ...message,
        status: 'sending' as const,
        tempId: `temp_${Date.now()}`,
      };
      
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: [...groupMessages, optimisticMessage],
          },
        };
      });
      
      return optimisticMessage.tempId;
    },

    updateMessageStatus: (groupId, tempId, realMessage) => {
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        const updatedMessages = groupMessages.map(msg => {
          if (msg.tempId === tempId) {
            return {
              ...realMessage,
              status: 'sent' as const,
            };
          }
          return msg;
        });
        
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: updatedMessages,
          },
        };
      });
    },

    // Flip an optimistic message to 'sent' without a server payload. Used as a
    // fallback when the send is acked but returns no message body, so the
    // message never gets stuck on the 'sending' (clock) state.
    markMessageSent: (groupId, tempId) => {
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        const updatedMessages = groupMessages.map((msg) =>
          msg.tempId === tempId ? { ...msg, status: 'sent' as const } : msg
        );

        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: updatedMessages,
          },
        };
      });
    },

    markMessageFailed: (groupId, tempId) => {
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        const updatedMessages = groupMessages.map(msg => {
          if (msg.tempId === tempId) {
            return {
              ...msg,
              status: 'failed' as const,
            };
          }
          return msg;
        });
        
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: updatedMessages,
          },
        };
      });
    },

    removeOptimisticMessage: (groupId, tempId) => {
      set((state) => {
        const groupMessages = state.messages[groupId] || [];
        const updatedMessages = groupMessages.filter(msg => msg.tempId !== tempId);
        
        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: updatedMessages,
          },
        };
      });
    },

    // Utilities
    clearAllData: () => {
      set({
        ...initialState,
        isConnected: get().isConnected, // Preserve connection status
        connectionError: get().connectionError,
      });
    },

    getGroupMessages: (groupId) => {
      return get().messages[groupId] || [];
    },

    getGroupOnlineUsers: (groupId) => {
      return get().onlineUsers[groupId] || [];
    },

    isHistoryLoaded: (groupId) => {
      return get().messageHistory[groupId] || false;
    },

    hasMoreHistoryMessages: (groupId) => {
      return get().hasMoreMessages[groupId] || false;
    },

    loadChatHistory: async (groupId) => {
      const state = get();
      if (state.messageHistory[groupId] || state.isLoadingHistory) {
        return; // Already loaded or loading
      }

      set({ isLoadingHistory: true });
      
      try {
        // Import the groupService dynamically to avoid circular dependencies
        const { groupService } = await import('../services/groupService');
        
        // Load recent 50 messages for initial chat history
        const response = await groupService.getGroupMessages(parseInt(groupId), {
          limit: 50
        });
        
        // Transform to chat store format
        const messages: ChatMessage[] = response.messages.map((msg) => ({
          id: msg.id.toString(),
          content: msg.content,
          type: 'text' as const,
          messageType: msg.messageType || 'TEXT',
          gifUrl: msg.gifUrl || null,
          gifId: msg.gifId || null,
          groupId: groupId,
          userId: msg.userId.toString(),
          username: msg.user.username,
          userProfilePhoto: msg.user.profilePhoto,
          userSuspendedAt: msg.user.suspendedAt,
          createdAt: msg.createdAt,
        }));
        
        
        set((state) => ({
          ...state,
          messages: {
            ...state.messages,
            [groupId]: messages
          },
          messageHistory: {
            ...state.messageHistory,
            [groupId]: true,
          },
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [groupId]: response.hasMore,
          },
          isLoadingHistory: false,
        }));
      } catch (error) {
        console.error('Failed to load chat history:', error);
        set({ 
          isLoadingHistory: false,
          connectionError: error instanceof Error ? error.message : 'Failed to load chat history'
        });
      }
    },

    // Merge server messages by id: add any the store doesn't already have,
    // keep existing ones untouched (so optimistic 'sending'/'sent' state and
    // any locally-known message survive). Adds messages both BEFORE and AFTER
    // the last known one, then re-sorts chronologically.
    mergeServerMessages: (groupId, messages) => {
      set((state) => {
        const existing = state.messages[groupId] || [];
        const existingIds = new Set(existing.map((m) => m.id).filter((id) => id !== ''));
        const toAdd = messages.filter((m) => m.id !== '' && !existingIds.has(m.id));

        if (toAdd.length === 0) {
          return state;
        }

        const merged = [...existing, ...toAdd].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );

        return {
          ...state,
          messages: {
            ...state.messages,
            [groupId]: merged,
          },
        };
      });
    },

    // Refetch the most recent messages for a group and merge them in. Called
    // on socket reconnect to recover messages missed during the outage.
    refetchRecentMessages: async (groupId) => {
      try {
        const { groupService } = await import('../services/groupService');
        const response = await groupService.getGroupMessages(parseInt(groupId), {
          limit: 50,
        });

        const messages: ChatMessage[] = response.messages.map((msg) => ({
          id: msg.id.toString(),
          content: msg.content,
          type: 'text' as const,
          messageType: msg.messageType || 'TEXT',
          gifUrl: msg.gifUrl || null,
          gifId: msg.gifId || null,
          groupId: groupId,
          userId: msg.userId.toString(),
          username: msg.user.username,
          userProfilePhoto: msg.user.profilePhoto,
          userSuspendedAt: msg.user.suspendedAt,
          createdAt: msg.createdAt,
          status: 'sent' as const,
        }));

        get().mergeServerMessages(groupId, messages);
        // Mark history as loaded in case the initial load never ran this session.
        set((state) => ({
          ...state,
          messageHistory: {
            ...state.messageHistory,
            [groupId]: true,
          },
        }));
      } catch (error) {
        console.error('Failed to refetch recent messages:', error);
      }
    },

    loadMoreMessages: async (groupId) => {
      const state = get();
      if (!state.hasMoreMessages[groupId] || state.isLoadingMore) {
        return; // No more messages or already loading
      }

      set({ isLoadingMore: true });
      
      try {
        // Import the groupService dynamically to avoid circular dependencies
        const { groupService } = await import('../services/groupService');
        
        // Get the oldest message ID for pagination
        const existingMessages = state.messages[groupId] || [];
        const oldestMessage = existingMessages[0]; // Messages are sorted by date (oldest first)
        
        if (!oldestMessage) {
          set({ isLoadingMore: false });
          return;
        }

        // Load older messages using cursor pagination (ID-based)
        const response = await groupService.getGroupMessages(parseInt(groupId), {
          limit: 25, // Load 25 older messages at a time
          cursor: oldestMessage.id // Use message ID as cursor for backend pagination
        });
        
        // Transform to chat store format
        const newMessages: ChatMessage[] = response.messages.map((msg) => ({
          id: msg.id.toString(),
          content: msg.content,
          type: 'text' as const,
          messageType: msg.messageType || 'TEXT',
          gifUrl: msg.gifUrl || null,
          gifId: msg.gifId || null,
          groupId: groupId,
          userId: msg.userId.toString(),
          username: msg.user.username,
          userProfilePhoto: msg.user.profilePhoto,
          userSuspendedAt: msg.user.suspendedAt,
          createdAt: msg.createdAt,
        }));
        
        
        // Prepend the new messages to existing messages
        const combinedMessages = [...newMessages, ...existingMessages];
        
        // Remove any potential duplicates based on message ID
        const uniqueMessages = combinedMessages.reduce((acc, message) => {
          if (!acc.find(m => m.id === message.id)) {
            acc.push(message);
          }
          return acc;
        }, [] as ChatMessage[]);
        
        // Ensure messages are sorted by date (oldest first)
        uniqueMessages.sort((a, b) => 
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
        
        
        set((state) => ({
          ...state,
          messages: {
            ...state.messages,
            [groupId]: uniqueMessages
          },
          hasMoreMessages: {
            ...state.hasMoreMessages,
            [groupId]: response.hasMore,
          },
          isLoadingMore: false,
        }));
      } catch (error) {
        console.error('Failed to load more messages:', error);
        set({ 
          isLoadingMore: false,
          connectionError: error instanceof Error ? error.message : 'Failed to load more messages'
        });
      }
    },
  }))
);

// Helper function to transform backend message format to frontend format
export function transformBackendMessage(backendMessage: any): ChatMessage {
  return {
    id: backendMessage.id,
    content: backendMessage.content,
    type: backendMessage.type || 'text',
    messageType: backendMessage.messageType || 'TEXT',
    ...(backendMessage.gifUrl && { gifUrl: backendMessage.gifUrl }),
    ...(backendMessage.gifId && { gifId: backendMessage.gifId }),
    groupId: backendMessage.groupId,
    userId: backendMessage.userId,
    username: backendMessage.username,
    userProfilePhoto: backendMessage.userProfilePhoto,
    userSuspendedAt: backendMessage.userSuspendedAt,
    createdAt: backendMessage.createdAt,
  };
}

// Helper function to transform backend online user format to frontend format
export function transformBackendOnlineUser(backendUser: any): OnlineUser {
  return {
    id: backendUser.id,
    username: backendUser.username,
    profilePhoto: backendUser.profilePhoto,
  };
}